import React, { useState, useEffect, useMemo } from 'react'
import { apiClient, getLottoBallColorHex, getReadableTextColor } from '@workspace/public-utils'
import { Seo } from '../../components/Seo'

interface GeneratedNumber {
  id: string
  numbers: number[]
  generated_at: string
  target_round?: number
  is_promotion?: boolean
  prize_rank?: number | null
  prize_name?: string | null
}

interface RoundGroup {
  round: number | null
  label: string
  items: GeneratedNumber[]
  latestDate: string
}

const MyNumbersPage: React.FC = () => {
  const [generatedNumbers, setGeneratedNumbers] = useState<GeneratedNumber[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(50)
  const [total, setTotal] = useState<number>(0)
  const [collapsedRounds, setCollapsedRounds] = useState<Set<string>>(new Set())
  const seo = <Seo title="내 번호 관리" canonicalPath="/dashboard/numbers" noindex />

  useEffect(() => {
    fetchGeneratedNumbers(page, perPage)
  }, [page, perPage])

  const fetchGeneratedNumbers = async (p: number, size: number) => {
    try {
      const skip = (p - 1) * size
      const response = await apiClient.get('/api/v1/auth/me/generated-numbers', {
        params: { skip, limit: size }
      })
      const data = response.data
      setGeneratedNumbers(
        (data.generated_numbers || []).map((it: any) => ({
          id: it.id,
          numbers: it.numbers,
          generated_at: it.generated_at,
          target_round: it.round_target,
          is_promotion: Boolean(it.is_promotion),
          prize_rank: it.prize_rank ?? null,
          prize_name: it.prize_name ?? null,
        }))
      )
      setTotal(Number(data.total || 0))
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const roundGroups = useMemo<RoundGroup[]>(() => {
    const map = new Map<string, GeneratedNumber[]>()
    for (const item of generatedNumbers) {
      const key = item.target_round != null ? String(item.target_round) : '__none__'
      const arr = map.get(key)
      if (arr) arr.push(item)
      else map.set(key, [item])
    }

    const groups: RoundGroup[] = []
    for (const [key, items] of map) {
      const round = key === '__none__' ? null : Number(key)
      items.sort((a, b) => a.generated_at.localeCompare(b.generated_at))
      const latest = items[items.length - 1].generated_at
      groups.push({
        round,
        label: round != null ? `${round}회` : '회차 미지정',
        items,
        latestDate: latest,
      })
    }

    groups.sort((a, b) => {
      if (a.round == null && b.round == null) return 0
      if (a.round == null) return 1
      if (b.round == null) return -1
      return b.round - a.round
    })

    return groups
  }, [generatedNumbers])

  const toggleRound = (key: string) => {
    setCollapsedRounds((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        {seo}
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">번호를 불러오는 중...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        {seo}
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null)
              setLoading(true)
              fetchGeneratedNumbers(page, perPage)
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-500"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8">
      {seo}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">내 번호 관리</h1>
        </div>

        {generatedNumbers.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow dark:shadow-none dark:border dark:border-gray-800 px-6 py-12 text-center text-gray-500 dark:text-gray-400">
            아직 생성된 번호가 없습니다.
          </div>
        ) : (
          <div className="space-y-4">
            {roundGroups.map((group) => {
              const groupKey = group.round != null ? String(group.round) : '__none__'
              const isCollapsed = collapsedRounds.has(groupKey)

              return (
                <div
                  key={groupKey}
                  className="bg-white dark:bg-gray-900 rounded-lg shadow dark:shadow-none dark:border dark:border-gray-800 overflow-hidden"
                >
                  {/* Round header */}
                  <button
                    type="button"
                    onClick={() => toggleRound(groupKey)}
                    className="w-full px-5 py-3.5 flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border-b border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 text-white text-xs font-bold">
                        {group.round ?? '—'}
                      </span>
                      <span className="text-base font-semibold text-gray-900 dark:text-white">
                        {group.label}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {group.items.length}세트
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(group.latestDate).toLocaleDateString('ko-KR')}
                      </span>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Number sets within the round */}
                  {!isCollapsed && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {group.items.map((item, idx) => (
                        <div key={item.id} className="px-5 py-3 flex items-center gap-4">
                          <span className="w-7 text-center text-xs font-medium text-gray-400 dark:text-gray-500 shrink-0">
                            {idx + 1}
                          </span>
                          <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                            {item.numbers.map((number, ni) => {
                              const bg = getLottoBallColorHex(number)
                              const fg = getReadableTextColor(bg)
                              return (
                                <span
                                  key={ni}
                                  className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full text-sm font-semibold shadow-sm"
                                  style={{ backgroundColor: bg, color: fg }}
                                >
                                  {number}
                                </span>
                              )
                            })}
                          </div>
                          <div className="ml-auto flex items-center gap-2">
                            {item.prize_rank && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                당첨 · {item.prize_name || `${item.prize_rank}등`}
                              </span>
                            )}
                            {item.is_promotion && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                프로모션
                              </span>
                            )}
                            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap hidden sm:inline">
                              {new Date(item.generated_at).toLocaleDateString('ko-KR')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        <div className="mt-6 bg-white dark:bg-gray-900 rounded-lg shadow dark:shadow-none dark:border dark:border-gray-800 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            총 {total.toLocaleString()}세트 / 페이지 {page}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >이전</button>
            <select
              className="px-2 py-1 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
              value={perPage}
              onChange={(e) => { setPage(1); setPerPage(parseInt(e.target.value, 10) || 10) }}
            >
              {[20, 50, 100].map(sz => (
                <option key={sz} value={sz}>{sz}개씩</option>
              ))}
            </select>
            <button
              className="px-3 py-1 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              onClick={() => {
                const maxPage = Math.max(1, Math.ceil(total / perPage))
                setPage((p) => Math.min(maxPage, p + 1))
              }}
              disabled={page >= Math.max(1, Math.ceil(total / perPage))}
            >다음</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MyNumbersPage
